import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild, inject, OnInit, Input } from '@angular/core';
import { AuthService } from '../../../core/services/auth.service';
import { Router } from '@angular/router';
import { StorageConstants, TranslatePipe } from '@techextensor/tab-core-utility';
import { PermissionService } from '../../../core/services/permission.service';
import { environment } from '../../../../environments/environment';
import { OrgAppInfoHelperService } from '../../../core/services/org-app-info-helper.service';
import { TabSdk } from '@techextensor/tab-sdk';
import { TranslationToken } from '../../../core/const/translation.token';

@Component({
    selector: 'app-user-drop-down',
    templateUrl: './user-drop-down.component.html',
    styleUrl: './user-drop-down.component.scss',
    imports: [CommonModule, TranslatePipe]
})
export class UserDropDownComponent implements OnInit {
  isUserPopupVisible = true;
  canShowOrgSettings = false;
  canShowRebuild = false;
  showLoader: boolean = false;
  private _router: Router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _orgAppInfoHelper = inject(OrgAppInfoHelperService);
  @ViewChild('userPopup') popup: ElementRef | undefined;
  loginData: any;
  roleNames: string = '';
  @Input() isAdminRoute: boolean = false;

  /**
   * Initializes the component after Angular initializes the data-bound input properties.
   * The ngOnInit() method is called once, after the constructor, and immediately after the
   * first ngOnChanges().
   * It is an appropriate place to put initialization code.
   */
  ngOnInit() {
    this.canShowOrgSettings = (
      this._permissionService.doesUserIsTabAdministrator() || this._permissionService.doesUserIsTenantAdministrator()
    ) && !this.isAdminRoute;

    const appInfo = TabSdk.store.getAppInfo() ?? {};
    this.canShowRebuild = appInfo?.TenantId?.Id?.toLowerCase() === TabSdk.store.tenantId?.toLowerCase() && (
      this._permissionService.doesUserIsAppAdmin() ||
      this._permissionService.doesUserIsTabAdministrator()
    ) && !this.isAdminRoute;

    // Fetch the login data from local storage and assign it to the loginData property.
    this.fetchLoginData();
  }

  /**
   * Fetches and processes login data from local storage.
   * This includes retrieving the user's personal data and roles,
   * and setting them to the component's properties.
   */
  fetchLoginData() {
    // Retrieve the login data from local storage
    const data = TabSdk.store.getUserInfo();

    if (data) {
      // Retrieve the current session details from session storage
      const currentTenantId = TabSdk.store.tenantId;
      const currentAppId = TabSdk.store.appId;
      const currentEnvId = TabSdk.store.environmentId;

      // Parse and assign the user's personal data
      this.loginData = data?.user?.PersonData;
      let currentRoles = '';

      // Iterate over each tenant's roles
      Object.entries(data.user.Roles).forEach(([tenantId, tenantData]: [any, any]) => {
        // Check if the tenant ID matches the current tenant ID
        if (currentTenantId?.toLowerCase() == tenantId.toLowerCase()) {
          // Iterate over each app's roles within the tenant
          Object.entries(tenantData).forEach(([appId, appData]: [any, any]) => {
            // Check if the app ID matches the current app ID
            if (
              currentAppId?.toLowerCase() == appId.toLowerCase() ||
              appId.toLowerCase() === environment.commonConfig.applicationId.toLowerCase()
            ) {
              // Iterate over each environment's roles within the app
              Object.entries(appData).forEach(([envId, roles]: [any, any]) => {
                // Check if the environment ID matches the current environment ID
                if (
                  !currentEnvId ||
                  (currentEnvId.toLowerCase() === envId.toLowerCase()) ||
                  (
                    appId.toLowerCase() === environment.commonConfig.applicationId.toLowerCase() &&
                    environment.id.toLowerCase() === envId.toLowerCase()
                  )
                ) {
                  // Concatenate role names into a comma-separated string
                  const roleNames = roles.map((role: any) => role.RoleName).join(', ');
                  if (currentRoles?.length) {
                    currentRoles += ', ' + roleNames;
                  } else {
                    currentRoles = roleNames;
                  }
                }
              });
            }
          });
        }
      });

      // Remove duplicate roles and assign them to the roleNames property
      this.roleNames = Array.from(new Set(currentRoles?.split(', ') ?? ''))?.join(', ');
    }
  }

  /**
   * Closes the user dropdown popup.
   *
   * This function sets the `isUserPopupVisible` property to `false`,
   * which hides the user dropdown popup when called.
   */
  closeUserPopup() {
    // Set the 'isUserPopupVisible' property to false to hide the user dropdown popup
    this.isUserPopupVisible = false;
  }

  @HostListener('document:mousedown', ['$event'])
  /**
   * Listen for a click event on the document and check if the click was outside the user dropdown popup.
   * If the click was outside the popup, close the popup.
   *
   * @param {any} event - The click event.
   */
  onOutsideClick(event: any) {
    // Check if the user dropdown is visible and the click is outside the popup
    if (this.isUserPopupVisible && this.popup && !this.popup.nativeElement.contains(event.target)) {
      // Close the user dropdown popup
      this.closeUserPopup();
    }
  }

  /**
   * Navigates to the change-password page
   * when the "Reset Password" item is clicked.
   */
  onResetPassword() {
    this._router.navigate([`${TabSdk.store.appName}/change-password`]);
    this.closeUserPopup();
  }

  /**
   * Signs out the user by calling the signOut method of the AuthService.
   * Upon successful sign out, navigates to the sign-in page.
   */
  onSignout() {
    // Call the signOut method of the AuthService and subscribe to the returned Observable
    this.authService.signOut(false).subscribe(() => {
      // Upon successful sign out, navigate to the sign-in page
      // this._router.navigate(['sign-in']);
      this.closeUserPopup();
    });
  }
  getProfile() {
    const orgInfo = TabSdk.store.orgLocaleSettings;
    if (orgInfo && orgInfo.profileScreenID) {
      if (orgInfo.profileScreenID) {
        // If profileScreenID exists, navigate to the specified screen
        const applicationCode = TabSdk.store.appName;
        this._router.navigateByUrl(
          `/${applicationCode}/screen/` + orgInfo?.profileScreenID + '?Id=' + TabSdk.store.userId
        );
        this.closeUserPopup();
      } else {
      }
    } else {
    }
  }
  onOraganizationSetting() {
    const appInfo = TabSdk.store.getAppInfo() ?? {};
    const appEnv = TabSdk.store.environmentId;
    TabSdk.store.setSession(StorageConstants.appNavigationMapping, { appInfo, appEnv });

    const appDetail = {
      AppName: environment.commonConfig.applicationCode,
      DisplayName: environment.commonConfig.applicationCode,
      Id: environment.commonConfig.applicationId,
      TenantId: { Id: environment.commonConfig.tenantId }
    };
    this._orgAppInfoHelper.setAppInfo(appDetail);
    TabSdk.store.environmentId = environment.id;
    this._router.navigate(['admin']);
  }

  /**
   * Rebuilds the application by calling the rebuildApp method from _appHelper service.
   * If the rebuild is successful (status code 200), it clears the IndexedDB and stores the schema into IndexedDB.
   */
  async rebuildApp() {
    try {
      this.showLoader = true;
      await TabSdk.util.loadApp({ rebuild: true });
      TabSdk.ui.showSuccess({ message: TranslationToken.RebuildSuccess });
      
    } catch (error) {
      TabSdk.ui.showError({ message: TranslationToken.RebuildFailed });
    } finally {
      this.showLoader = false;
    }
  }
}
